import gsap from 'gsap'
import Texture2D from 'nanogl/texture-2d'
import Animation from 'nanogl-gltf/lib/elements/Animation'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import MeshRenderer from 'nanogl-gltf/lib/renderer/MeshRenderer'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { MusicAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'
import { HexToVec3 } from '@/webgl/core/Color'
import Deferred from '@/webgl/core/Deferred'
import { RenderContext } from '@/webgl/core/RenderContext'
import Signal from '@/webgl/core/Signal'
import { clamp } from '@/webgl/math'
import AssetDatabase from '@/webgl/resources/AssetDatabase'
import GltfResource from '@/webgl/resources/GltfResource'
import LanesMergeAnimation from '@/webgl/runner/animations/LanesMergeAnimation'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import ExpressionMaskChunk from '@/webgl/runner/rendering/chunks/ExpressionMaskChunk'
import { backgroundLayer } from '@/webgl/runner/rendering/RenderingUtils'
import ScreenSpiralRenderer from '@/webgl/runner/rendering/ScreenSpiralRenderer'
import { CARIS_1_SCROLL_MASK_WORLD } from '@/webgl/runner/scenarios/caris_1/Caris1Setup'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import MusicSystem from '@/webgl/runner/systems/MusicSystem'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

export default class MaskDoorSegment implements IGameSegment {
  world: GameWorld
  renderer: IRunnerRenderer

  onComplete: Signal<void> = new Signal()

  doorGltf: Gltf
  doorAnimation: Animation
  doorRender: boolean = false
  doorAnimationTime: number = 0
  doorAnimationDuration: number = 0

  spiralTex: Texture2D
  screenSpiral: ScreenSpiralRenderer

  laneMerge: LanesMergeAnimation

  eyeGltf: Gltf
  spiralEye: MeshRenderer
  stopped: boolean = true
  entered: boolean = false

  constructor(readonly id: string = 'mask_door') {}

  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup): void {
    this.world = world
    this.renderer = renderer

    this.laneMerge = new LanesMergeAnimation(renderer.lanes)

    // this.doorSpiralChunk = new PortalSpiralChunk(world.time);
    // this.doorSpiralChunk.rotateFactor = 0.15;

    // HexToVec3(0x06748D, this.doorSpiralChunk.color1);
    // HexToVec3(0x4EA4BD, this.doorSpiralChunk.color2);

    this.screenSpiral = new ScreenSpiralRenderer(renderer.gl)
    this.screenSpiral.rotate = false

    HexToVec3(0x06748d, this.screenSpiral.color1)
    HexToVec3(0x4ea4bd, this.screenSpiral.color2)

    this.entered = false
  }

  async load(): Promise<void> {
    await this.loadSpiral()
    await this.loadEye()
    await this.loadDoor()
  }

  async loadSpiral() {
    this.spiralTex = await AssetDatabase.getTexture('caris_1/face_spiral.jpg', this.renderer.gl, {
      wrap: 'clamp',
    }).load()
    // this.doorSpiralChunk.setTexture(this.spiralTex);
    this.screenSpiral.setTexture(this.spiralTex)
  }

  async loadEye() {
    const overrides = new MaterialOverrideExtension()

    const chunk = new ExpressionMaskChunk(this.world.time, 4, 3)

    overrides.overridePass('expression', (ctx, m) => {
      const pass = m.getPass('color').pass as UnlitPass
      backgroundLayer(this.renderer.gl, pass)
      pass.inputs.add(chunk)
      return pass
    })

    overrides.overridePass('mask', (ctx, m) => {
      const pass = m.getPass('color').pass as UnlitPass
      backgroundLayer(this.renderer.gl, pass)
      return pass
    })

    overrides.overridePass('lines', (ctx, m) => {
      const pass = m.getPass('color').pass as UnlitPass
      backgroundLayer(this.renderer.gl, pass)
      return pass
    })

    this.eyeGltf = await new GltfResource('caris_1/mask_world_eye.glb', this.renderer.gl, {
      extensions: [overrides],
    }).load()

    this.spiralEye = this.eyeGltf.getElementByName(GltfTypes.NODE, 'mask').renderable
  }

  async loadDoor() {
    const overrides = new MaterialOverrideExtension()

    // overrides.overridePass('portal', (ctx, m) => {
    //   const pass = m.getPass('color').pass as UnlitPass;
    //   // pass.inputs.add(this.doorSpiralChunk);
    //   const color = HexToNewVec3(0x06748D);
    //   console.log(pass);
    //   (pass.baseColorFactor.param as Uniform).set(...color)
    //   return pass;
    // });

    this.doorGltf = await new GltfResource('caris_1/mask_world_door.glb', this.renderer.gl, {
      extensions: [overrides],
    }).load()

    this.doorAnimation = this.doorGltf.animations[0]
    this.doorAnimationTime = 0
    this.doorAnimationDuration = this.doorAnimation.duration
  }

  async start(): Promise<void> {
    this.world.getTrackingSystem().trackImpression({
      unitName: `caris_simulation_0`,
      unitVariation: `scenario_auto_obstacle_mask`,
      unitLocation: 'tyce',
    })

    this.stopped = false
    this.doorAnimationTime = 0
    const settings = this.world.settings

    this.doorRender = true
    this.doorGltf.root.z = -settings.LANE_LENGTH

    AudioEngine.play('TYCE-caris_lets-pretend-1')

    this.world.root.add(this.doorGltf.root)

    const player = this.world.player
    player.direction = 0
    player.lane = 0
    player.locked = true
    gsap.to(player.node, { x: 0, duration: 1.0 })

    this.laneMerge.init()
    await this.laneMerge.play()
    if (this.stopped) return

    // await Delay((this.doorAnimationDuration - this.doorAnimationTime) * 1000 - 500);
    // if (this.stopped) return;
  }

  async firstFlash(): Promise<void> {
    const defer = new Deferred<void>()
    gsap.to(this.screenSpiral, {
      reveal: 0.5,
      duration: 0.7,
      ease: 'linear',
      onComplete: () => defer.resolve(),
    })
    return defer.promise
  }

  async releaseFlash(): Promise<void> {
    const defer = new Deferred<void>()
    gsap.to(this.screenSpiral, {
      reveal: 1.0,
      duration: 1.0,
      ease: 'linear',
      onComplete: () => defer.resolve(),
    })
    return defer.promise
  }

  async enterMaskWorld() {
    this.entered = true
    await this.firstFlash()
    if (this.stopped) return

    this.world.systems.get<MusicSystem>(MusicSystem).play(MusicAudio.Masks)

    this.world.state.alterJauge(1)

    this.renderer.background.setColor(0xfed1c8)
    this.renderer.background.updateEye(this.spiralEye)
    this.renderer.player.setColors(0xfed1c8, 0xfed1c8, 0xfed1c8)

    this.renderer.lanes.setColor(0x4ea4bd)
    this.renderer.lanes.setMaskColor(0xfed1c8)
    this.renderer.eye.setScale(1.7)

    this.doorRender = false
    this.laneMerge.cleanup()

    this.world.background.activeItems = []
    this.world.background.spawnableItems = CARIS_1_SCROLL_MASK_WORLD

    await this.releaseFlash()

    this.onComplete.emit()
  }

  update(time: TimeEntity): void {
    const settings = this.world.settings
    const paused = settings.scrollSpeed == 0
    const factor = paused ? 0 : 1

    const z = this.doorGltf.root.z + time.unscaledDeltaTime * 2.3 * factor
    this.doorGltf.root.z = Math.min(z, -settings.GRAB_THRESHOLD - 0.2)

    this.doorAnimation.evaluate(this.doorAnimationTime)
    this.doorAnimationTime += time.unscaledDeltaTime * factor
    this.doorAnimationTime = clamp(this.doorAnimationTime, 0, this.doorAnimationDuration)

    if (this.doorAnimationTime == this.doorAnimationDuration && !this.entered) {
      this.enterMaskWorld()
    }
  }

  render(context: RenderContext): void {
    if (this.doorRender) {
      for (const renderable of this.doorGltf.renderables) {
        renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
      }
    }

    this.screenSpiral.render(context)
  }

  stop(): void {
    this.stopped = true
    this.world.player.locked = false
    this.world.root.remove(this.doorGltf.root)
  }
}
